import React, { useState, useEffect } from 'react';
import { Heart, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { donationsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { describeApiError } from '../../lib/apiError';
import { toast } from 'sonner';

const PRESETS_CENTS = [500, 1000, 2500, 5000, 10000];
const FREQUENCIES = [
  { value: 'one_time',  label: 'Don ponctuel' },
  { value: 'monthly',   label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'yearly',    label: 'Annuel' },
];

const DonationFormDialog = ({ open, quest, onClose }) => {
  const { user } = useAuth();
  const [amountCents, setAmountCents] = useState(2500);
  const [customAmount, setCustomAmount] = useState('');
  const [frequency, setFrequency] = useState('one_time');
  const [anonymous, setAnonymous] = useState(false);
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // reset state on open
  useEffect(() => {
    if (open) {
      setAmountCents(2500);
      setCustomAmount('');
      setFrequency('one_time');
      setAnonymous(false);
      setDonorName(user?.name || '');
      setDonorEmail(user?.email || '');
      setMessage('');
      setSuccess(null);
    }
  }, [open, user]);

  const finalAmountCents = customAmount
    ? Math.round(parseFloat(customAmount.replace(',', '.')) * 100) || 0
    : amountCents;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (finalAmountCents < 100) {
      toast.error('Montant minimum : 1 €');
      return;
    }
    setSubmitting(true);
    try {
      const r = await donationsApi.start({
        quest_id: quest?.id || null,
        amount_cents: finalAmountCents,
        currency: 'EUR',
        frequency,
        anonymous,
        donor_name: anonymous ? null : (donorName || null),
        donor_email: anonymous ? null : (donorEmail || null),
        message: message || null,
      });
      const data = r.data;
      if (data.status === 'stripe_disabled') {
        setSuccess({
          kind: 'stripe_disabled',
          message: data.message,
          donationId: data.donation_id,
        });
      } else if (data.status === 'ready' && data.checkout_url) {
        // Future: redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        setSuccess({ kind: 'other', message: 'Don enregistré', donationId: data.donation_id });
      }
    } catch (err) {
      const { message } = describeApiError(err);
      toast.error(`Don impossible : ${message}`);
      // eslint-disable-next-line no-console
      console.error('Donation start error:', err?.response?.data || err);
    } finally {
      setSubmitting(false);
    }
  };

  const title = quest === null
    ? 'Soutenir Noor El Bayen'
    : quest
      ? `Faire un don pour : ${quest.title_fr}`
      : '';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="donation-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary" />{title}
          </DialogTitle>
          <DialogDescription>
            Tous les dons sont en euros (€). Vous recevrez une confirmation par email.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <span className="text-amber-200/90">{success.message}</span>
            </div>
            <div className="flex items-center gap-2 text-emerald-400 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Don enregistré (ID : <span className="font-mono">{success.donationId.slice(-8)}</span>)
            </div>
            <DialogFooter>
              <Button onClick={onClose} className="bg-primary text-background" data-testid="donation-success-close">Fermer</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Amount presets */}
            <div>
              <Label className="text-xs text-text-secondary">Montant</Label>
              <div className="grid grid-cols-5 gap-2 mt-1">
                {PRESETS_CENTS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { setAmountCents(c); setCustomAmount(''); }}
                    className={`py-2 rounded border text-sm transition-colors ${
                      !customAmount && amountCents === c
                        ? 'bg-primary text-background border-primary'
                        : 'border-border text-text-primary hover:bg-surface-elevated'
                    }`}
                    data-testid={`donation-preset-${c}`}
                  >
                    {c / 100}€
                  </button>
                ))}
              </div>
              <Input
                type="number"
                min={1}
                step="0.5"
                placeholder="Ou montant personnalisé (€)"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="mt-2 bg-surface border-border"
                data-testid="donation-custom-amount"
              />
            </div>

            {/* Frequency */}
            <div>
              <Label className="text-xs text-text-secondary">Fréquence</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                {FREQUENCIES.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setFrequency(f.value)}
                    className={`py-2 rounded border text-xs transition-colors ${
                      frequency === f.value
                        ? 'bg-primary text-background border-primary'
                        : 'border-border text-text-primary hover:bg-surface-elevated'
                    }`}
                    data-testid={`donation-freq-${f.value}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Anonymous toggle */}
            <div className="flex items-center justify-between border border-border rounded p-3">
              <div>
                <div className="text-sm text-text-primary">Donner anonymement</div>
                <div className="text-xs text-text-muted">Votre nom n'apparaîtra pas publiquement.</div>
              </div>
              <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="donation-anonymous-switch" />
            </div>

            {!anonymous && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-text-secondary">Nom (facultatif)</Label>
                  <Input value={donorName} onChange={(e) => setDonorName(e.target.value)} className="bg-surface border-border" data-testid="donation-name" />
                </div>
                <div>
                  <Label className="text-xs text-text-secondary">Email (facultatif)</Label>
                  <Input type="email" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} className="bg-surface border-border" data-testid="donation-email" />
                </div>
              </div>
            )}

            {/* Message */}
            <div>
              <Label className="text-xs text-text-secondary">Message (facultatif)</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Une dédicace, une intention…"
                className="bg-surface border-border"
                data-testid="donation-message"
              />
              <div className="text-[10px] text-text-muted mt-1 text-right">{message.length}/500</div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Annuler</Button>
              <Button type="submit" disabled={submitting || finalAmountCents < 100} className="bg-primary text-background" data-testid="donation-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Heart className="w-4 h-4 mr-2" />}
                Donner {(finalAmountCents / 100).toFixed(2)} €
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DonationFormDialog;
